import { useFormik } from 'formik';
import { get } from 'lodash';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import { TextFiled, CheckBox, Radio, TextArea, DatePicker, Select } from 'tsx/comps/form';
import Switch from 'tsx/comps/form/Switch';
import { media, colors } from 'tsx/theme';
import { PHONE_REGEX } from 'tsx/util';
import * as Yup from 'yup';
import { countries } from '../OverSixtyForm/data';
import { apiUrl } from 'tsx/util';

// Validator for date in dd/mm/yyyy format
const dateValidator = Yup.string()
  .required('Required')
  .matches(/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/, 'Must be in dd/mm/yyyy format')
  .test('valid-date', 'Invalid date', (value) => {
    if (!value) return false;

    const [day, month, year] = value.split('/').map(Number);
    const date = new Date(year, month - 1, day);

    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  });

const dateFormatValidator = (schema: Yup.StringSchema) =>
  schema
    .matches(/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/, 'Must be in dd/mm/yyyy format')
    .test('valid-date', 'Invalid date', function (value) {
      if (!value) return true;
      const [day, month, year] = value.split('/').map(Number);
      const date = new Date(year, month - 1, day);
      return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
    });

// Convert dd/mm/yyyy to ISO date (yyyy-mm-dd) for API compatibility
const toIsoDateString = (value?: string) => {
  if (!value) return value;

  const [day, month, year] = value.split('/').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (isNaN(date.getTime())) {
    return value;
  }

  return date.toISOString().split('T')[0];
};

// ---- Reusable test helpers ----

const requiredIfSource = (sourceKey: string, fromLevel: number = 1) =>
  Yup.string().test('required-if-source', 'Required', function (value) {
    const isActive = this.from?.[fromLevel]?.value?.[sourceKey];
    if (!isActive) return true;
    if (!value) return this.createError({ message: 'Required' });
    if (value.length > 255) return this.createError({ message: 'Max 255 characters' });
    return true;
  });

const dateIfSource = (sourceKey: string, fromLevel: number = 1) =>
  Yup.string().test('date-if-source', 'Required', function (value) {
    const isActive = this.from?.[fromLevel]?.value?.[sourceKey];
    if (!isActive) return true;
    if (!value) return this.createError({ message: 'Required' });
    const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
    if (!dateRegex.test(value)) return this.createError({ message: 'Must be in dd/mm/yyyy format' });
    const [day, month, year] = value.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    if (!(date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day)) {
      return this.createError({ message: 'Invalid date' });
    }
    return true;
  });

// ---- Sources validation ----

const dateFields = [
  'primaryAccountHolder.dateOfBirth',
  'secondaryAccountHolder.dateOfBirth',
  'sources.savingsOrInvestments.dateFundsReceived',
  'sources.incomeFromEmploymentOrBonus.dateReceived',
  'sources.saleOfProperty.datePropertySold',
  'sources.inheritance.dateFundsReceived',
  'sources.gift.dateFundsReceived',
  'sources.gift.giftorDateOfBirth',
];

const convertDateFields = (data: any) => {
  dateFields.forEach((path) => {
    const segments = path.split('.');
    let ref = data;

    for (let i = 0; i < segments.length - 1; i++) {
      if (!ref) return;
      ref = ref[segments[i]];
    }

    const lastKey = segments[segments.length - 1];
    if (ref && typeof ref[lastKey] === 'string') {
      ref[lastKey] = toIsoDateString(ref[lastKey]);
    }
  });
};

const Section = styled.section`
  form {
    margin-top: 37px;
    background-color: ${colors.lp1};
    padding: 70px 160px;
    h2 {
      font-size: 26px;
    }
    h3 {
      font-size: 20px;
    }
    .radio-flex {
      display: flex;
      gap: 12px;
      grid-gap: 12px;
      align-items: center;
      margin-bottom: 18px;
    }
    .check-flex {
      display: flex;
      gap: 20px;
      grid-gap: 20px;
      align-items: center;
      margin-bottom: 18px;
    }

    .separator {
      opacity: 0.2;
      background: #632573;
      height: 1px;
    }

    .spc-18 {
      height: 18px;
    }
    .spc-30 {
      height: 30px;
    }

    .input-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 18px;
      &-inner {
        position: relative;
        input,
        select {
          width: 100%;
        }
      }
      .input-endorment {
        color: #fff;
        background-color: #632573;
        border-top-right-radius: 50px;
        border-bottom-right-radius: 50px;
        justify-content: center;
        align-items: center;
        width: 45px;
        height: 100%;
        padding-right: 3px;
        font-weight: 500;
        display: flex;
        position: absolute;
        right: 0;
        top: 0;
      }
      &-full {
        width: 100%;
        textarea {
          width: 100%;
        }
      }
    }
    .spc18 {
      height: 18px;
    }
  }
  .response {
    margin-top: 37px;
    background-color: ${colors.lp1};
    padding: 70px 160px;
    &-card {
      background: ${colors.colorWhite};
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      border-radius: 25px;
      p {
        max-width: 60%;
        line-height: 25px;
        text-align: center;
      }
    }
  }

  ${media.md(css`
    form {
      margin-top: 37px;
      background-color: ${colors.lp1};
      padding: 50px 20px;
      h2 {
        font-size: 20px;
      }
      h3 {
        font-size: 18px;
      }
    }
    .response {
      padding: 50px 20px;
      &-card {
        p {
          max-width: 80%;
          line-height: 25px;
          text-align: center;
        }
      }
    }
  `)};
  ${media.sm(css`
    form {
      .input-grid {
        grid-template-columns: 1fr;
      }
      h2 {
        font-size: 18px;
      }
      h3 {
        font-size: 12px;
      }
    }
    .response {
      &-card {
        border-radius: 0px;
        p {
          max-width: 100%;
          margin: 0px 30px;
          line-height: 19px;
        }
      }
    }
  `)};
`;

const chagedDetailsSchema = Yup.object({
  homeTelephone: Yup.boolean(),
  homeTelephoneNumber: Yup.string()
    .test('conditional-required', 'Required', function (value) {
      const { homeTelephone } = this.parent;
      if (homeTelephone) {
        return value ? true : false;
      }
      return true;
    })
    .matches(PHONE_REGEX, 'Invalid phone number')
    .nullable(),

  mobileTelephone: Yup.boolean(),
  mobileTelephoneNumber: Yup.string()
    .test('conditional-required', 'Required', function (value) {
      const { mobileTelephone } = this.parent;
      if (mobileTelephone) {
        return value ? true : false;
      }
      return true;
    })
    .matches(PHONE_REGEX, 'Invalid phone number')
    .nullable(),

  email: Yup.boolean(),
  emailAddress: Yup.string()
    .test('conditional-required', 'Required', function (value) {
      const { email } = this.parent;
      if (email) {
        return value ? true : false;
      }
      return true;
    })
    .email('Invalid email'),

  nationalityType: Yup.boolean(),
  nationality: Yup.string().test('conditional-required', 'Required', function (value) {
    const { nationalityType } = this.parent;
    if (nationalityType) {
      return value ? true : false;
    }
    return true;
  }),

  occupationType: Yup.boolean(),
  occupation: Yup.string().test('conditional-required', 'Required', function (value) {
    const { occupationType } = this.parent;
    if (occupationType) {
      return value ? true : false;
    }
    return true;
  }),

  annualIncomeAmount: Yup.boolean(),
  annualIncome: Yup.number()
    .max(999999999, 'Must be 9 digits or less')
    .test('conditional-required', 'Required', function (value) {
      const { annualIncomeAmount } = this.parent;
      if (annualIncomeAmount) {
        return value ? true : false;
      }
      return true;
    }),

  otherIncomeAmount: Yup.boolean(),
  otherIncome: Yup.number()
    .max(999999999, 'Must be 9 digits or less')
    .test('conditional-required', 'Required', function (value) {
      const { otherIncomeAmount } = this.parent;
      if (otherIncomeAmount) {
        return value ? true : false;
      }
      return true;
    }),
});

const validationSchema = Yup.object({
  accountNumber: Yup.string().max(255, 'Max 255 characters').required('Required'),
  numberOfAccounts: Yup.string().required('Required'),
  primaryAccountHolder: Yup.object({
    firstName: Yup.string().max(255, 'Max 255 characters').required('Required'),
    lastName: Yup.string().max(255, 'Max 255 characters').required('Required'),
    dateOfBirth: dateValidator,
    changedDetails: chagedDetailsSchema,
  }),
  secondaryAccountHolder: Yup.object({
    firstName: Yup.string().max(255, 'Max 255 characters').required('Required'),
    lastName: Yup.string().max(255, 'Max 255 characters').required('Required'),
    dateOfBirth: dateValidator,
    changedDetails: chagedDetailsSchema,
  }).nullable(),
  sources: Yup.object({
    sourceSavingsOrInvestments: Yup.boolean().required('Required'),
    savingsOrInvestments: Yup.object({
      dateFundsReceived: dateIfSource('sourceSavingsOrInvestments'),
      amountReceived: requiredIfSource('sourceSavingsOrInvestments'),
      fromCompany: requiredIfSource('sourceSavingsOrInvestments'),
      savingsAccumulationMethod: requiredIfSource('sourceSavingsOrInvestments'),
    }).required('Required'),

    sourceEmploymentOrBonus: Yup.boolean().required('Required'),
    incomeFromEmploymentOrBonus: Yup.object({
      dateReceived: dateIfSource('sourceEmploymentOrBonus'),
      amountReceived: requiredIfSource('sourceEmploymentOrBonus'),
      fromEmployer: requiredIfSource('sourceEmploymentOrBonus'),
    }).required('Required'),

    sourceSaleOfProperty: Yup.boolean().required('Required'),
    saleOfProperty: Yup.object({
      line1: requiredIfSource('sourceSaleOfProperty'),
      line2: requiredIfSource('sourceSaleOfProperty'),
      city: requiredIfSource('sourceSaleOfProperty'),
      province: requiredIfSource('sourceSaleOfProperty'),
      postcode: requiredIfSource('sourceSaleOfProperty'),
      country: requiredIfSource('sourceSaleOfProperty'),
      datePropertySold: dateIfSource('sourceSaleOfProperty'),
      saleProceedsReceived: requiredIfSource('sourceSaleOfProperty'),
    }).required('Required'),
    sourceInheritance: Yup.boolean().required('Required'),
    inheritance: Yup.object({
      dateFundsReceived: dateIfSource('sourceInheritance'),
      amountReceived: requiredIfSource('sourceInheritance'),
      from: requiredIfSource('sourceInheritance'),
    }).required('Required'),

    sourceGift: Yup.boolean().required('Required'),
    gift: Yup.object({
      dateFundsReceived: dateIfSource('sourceGift'),
      amountReceived: requiredIfSource('sourceGift'),
      giftReason: requiredIfSource('sourceGift'),
      relationship: requiredIfSource('sourceGift'),
      giftorName: requiredIfSource('sourceGift'),
      giftorDateOfBirth: dateIfSource('sourceGift'),
      giftorAddress: Yup.object({
        line1: requiredIfSource('sourceGift', 2),
        line2: requiredIfSource('sourceGift', 2),
        city: requiredIfSource('sourceGift', 2),
        county: requiredIfSource('sourceGift', 2),
        postcode: requiredIfSource('sourceGift', 2),
        country: requiredIfSource('sourceGift', 2),
      }).required('Required'),
    }).required('Required'),

    otherSource: Yup.boolean().required('Required'),
    other: Yup.string().when('otherSource', {
      is: true,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
  }).required('Required'),
});

const App = () => {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const { values, handleChange, handleBlur, errors, touched, isValid, handleSubmit, isSubmitting, setFieldValue } =
    useFormik({
      validationSchema,
      initialValues: {
        accountNumber: '',
        numberOfAccounts: 1,
        primaryAccountHolder: {
          firstName: '',
          lastName: '',
          dateOfBirth: '',
          changedDetails: {
            homeTelephone: false,
            homeTelephoneNumber: '',
            mobileTelephone: false,
            mobileTelephoneNumber: '',
            email: false,
            emailAddress: '',
            nationalityType: false,
            nationality: '',
            occupationType: false,
            occupation: '',
            annualIncomeAmount: false,
            annualIncome: '',
            otherIncomeAmount: false,
            otherIncome: '',
          },
        },
        secondaryAccountHolder: null as null | {
          firstName: string;
          lastName: string;
          dateOfBirth: string;
          changedDetails: {
            homeTelephone: boolean;
            homeTelephoneNumber: string;
            mobileTelephone: boolean;
            mobileTelephoneNumber: string;
            email: boolean;
            emailAddress: string;
            nationalityType: boolean;
            nationality: string;
            occupationType: boolean;
            occupation: string;
            annualIncomeAmount: boolean;
            annualIncome: string;
            otherIncomeAmount: boolean;
            otherIncome: string;
          };
        },
        sources: {
          sourceSavingsOrInvestments: false,
          savingsOrInvestments: {
            dateFundsReceived: '',
            amountReceived: '',
            fromCompany: '',
            savingsAccumulationMethod: '',
          },
          sourceEmploymentOrBonus: false,
          incomeFromEmploymentOrBonus: {
            dateReceived: '',
            amountReceived: '',
            fromEmployer: '',
          },
          sourceSaleOfProperty: false,
          saleOfProperty: {
            line1: '',
            line2: '',
            city: '',
            province: '',
            postcode: '',
            country: '',
            datePropertySold: '',
            saleProceedsReceived: '',
          },
          sourceInheritance: false,
          inheritance: {
            dateFundsReceived: '',
            amountReceived: '',
            from: '',
          },
          sourceGift: false,
          gift: {
            dateFundsReceived: '',
            amountReceived: '',
            giftReason: '',
            relationship: '',
            giftorName: '',
            giftorDateOfBirth: '',
            giftorAddress: {
              line1: '',
              line2: '',
              city: '',
              county: '',
              postcode: '',
              country: '',
            },
          },
          otherSource: false,
          other: '',
        },
      },
      onSubmit: async (values) => {
        console.log(values);

        const fieldsToRemove = [
          'homeTelephone',
          'mobileTelephone',
          'email',
          'nationalityType',
          'occupationType',
          'annualIncomeAmount',
          'otherIncomeAmount',
        ];

        const sourceFieldsToRemove: { [key: string]: string } = {
          sourceSavingsOrInvestments: 'savingsOrInvestments',
          sourceEmploymentOrBonus: 'incomeFromEmploymentOrBonus',
          sourceSaleOfProperty: 'saleOfProperty',
          sourceInheritance: 'inheritance',
          sourceGift: 'gift',
          otherSource: 'other',
        };

        const valuesToSend = JSON.parse(JSON.stringify(values));

        convertDateFields(valuesToSend);
        if (valuesToSend.sources.saleOfProperty) {
          const { line1, line2, city, province, postcode, country, ...rest } = valuesToSend.sources.saleOfProperty;
          valuesToSend.sources.saleOfProperty = {
            ...rest,
            propertyAddress: { line1, line2, city, county: province, postcode, country },
          };
        }

        ['primaryAccountHolder', 'secondaryAccountHolder'].forEach((holder) => {
          if (valuesToSend[holder]) {
            fieldsToRemove.forEach((field) => {
              delete valuesToSend[holder].changedDetails[field];
            });
          }
        });

        Object.keys(sourceFieldsToRemove).forEach((field) => {
          if (!valuesToSend.sources[field]) {
            delete valuesToSend.sources[field];
            delete valuesToSend.sources[sourceFieldsToRemove[field]];
          } else {
            delete valuesToSend.sources[field];
          }
        });

        delete valuesToSend.numberOfAccounts;

        setIsFormSubmitted(true);

        try {
          const response = await fetch(`https://cbs-dev.radical-test.co.uk:44343/api/forms/overpayment-declaration`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(valuesToSend),
          });

          if (!response.ok) {
            console.error('Failed to submit form:', response.status, response.statusText);
            const responseData = await response.json();
            console.error('Response data:', responseData);
            return;
          }
        } catch (error) {
          console.error('An error occurred while submitting the form:', error);
        }
      },
    });
  console.log('formik errors', errors.sources);
  console.log(' values', values.sources);
  return (
    <Section>
      {!isFormSubmitted ? (
        <form onSubmit={handleSubmit}>
          <h2>Your Personal Details</h2>
          <div className="spacer-responsive-30" />
          <p>
            To enable us to continue to deliver great service to you, and ensure we comply with our regulatory
            requirements, it is important that the information we hold about you is accurate and up to date. So we can
            maintain our records, please complete and submit this short questionnaire.
          </p>
          <div className="spacer-responsive-30" />
          <TextFiled
            placeholder="Mortgage Account Number"
            id="accountNumber"
            $fullWidth
            value={values.accountNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.accountNumber && !!errors.accountNumber}
          />
          {touched.accountNumber && errors.accountNumber && (
            <div role="alert" aria-live="assertive" aria-relevant="all">
              <span className="visually-hidden"> Error: {errors.accountNumber}</span>
            </div>
          )}
          <div className="spacer-responsive-30" />
          <h3>Number of Account holders</h3>
          <div className="spacer-responsive-30" />
          <div className="radio-flex">
            <Radio
              name="numberOfAccounts"
              checked={values.numberOfAccounts === 1}
              onChange={() => {
                setFieldValue('numberOfAccounts', 1);
                setFieldValue('secondaryAccountHolder', null);
              }}
            />
            <p>1</p>
          </div>
          <div className="radio-flex">
            <Radio
              name="numberOfAccounts"
              checked={values.numberOfAccounts === 2}
              onChange={() => {
                setFieldValue('numberOfAccounts', 2);
                setFieldValue('secondaryAccountHolder', {
                  firstName: '',
                  lastName: '',
                  dateOfBirth: '',
                  changedDetails: {},
                });
              }}
            />
            <p>2</p>
          </div>
          <div className="spacer-responsive-30" />
          <div className="separator" />
          <div className="spacer-responsive-30" />
          <h3>Primary Account Holder</h3>
          <div className="spc-18" />
          <div className="input-grid">
            <div className="input-grid-inner">
              <TextFiled
                placeholder="First Name"
                id="primaryAccountHolder.firstName"
                value={values.primaryAccountHolder.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.primaryAccountHolder?.firstName && !!errors.primaryAccountHolder?.firstName}
              />
              {touched.primaryAccountHolder?.firstName && !!errors.primaryAccountHolder?.firstName && (
                <div role="alert" aria-live="assertive" aria-relevant="all">
                  <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.firstName}</span>
                </div>
              )}
            </div>
            <div className="input-grid-inner">
              <TextFiled
                placeholder="Last Name"
                id="primaryAccountHolder.lastName"
                value={values.primaryAccountHolder.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName}
              />
              {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                <div role="alert" aria-live="assertive" aria-relevant="all">
                  <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                </div>
              )}
            </div>
            <div className="input-grid-inner">
              <TextFiled
                placeholder="dd/mm/yyyy"
                id="primaryAccountHolder.dateOfBirth"
                value={values.primaryAccountHolder.dateOfBirth}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.primaryAccountHolder?.dateOfBirth && !!errors.primaryAccountHolder?.dateOfBirth}
              />
              {touched.primaryAccountHolder?.dateOfBirth && !!errors.primaryAccountHolder?.dateOfBirth && (
                <div role="alert" aria-live="assertive" aria-relevant="all">
                  <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.dateOfBirth}</span>
                </div>
              )}
            </div>
          </div>
          <div className="spc-30" />
          <p>
            <b>Have any of your details changed? If so please, complete, otherwise move to next section:</b>
          </p>
          <div className="spc-18" />
          {/* Home Telephone Number */}
          <div className="check-flex">
            <CheckBox
              checked={values.primaryAccountHolder.changedDetails.homeTelephone}
              onSelect={(val) => {
                if (!val) {
                  setFieldValue('primaryAccountHolder.changedDetails.homeTelephoneNumber', '');
                }
                setFieldValue('primaryAccountHolder.changedDetails.homeTelephone', val);
              }}
            />
            <p>Home Telephone Number</p>
          </div>
          {values.primaryAccountHolder.changedDetails.homeTelephone && (
            <>
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Home Telephone Number"
                    id="primaryAccountHolder.changedDetails.homeTelephoneNumber"
                    value={values.primaryAccountHolder.changedDetails.homeTelephoneNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'primaryAccountHolder.changedDetails.homeTelephoneNumber') &&
                        get(errors, 'primaryAccountHolder.changedDetails.homeTelephoneNumber'),
                    )}
                  />
                  {Boolean(
                    get(touched, 'primaryAccountHolder.changedDetails.homeTelephoneNumber') &&
                      get(errors, 'primaryAccountHolder.changedDetails.homeTelephoneNumber'),
                  ) && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden">
                        Error: {get(errors, 'primaryAccountHolder.changedDetails.homeTelephoneNumber')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="spc-30" />
            </>
          )}
          {/* Mobile Telephone Number */}
          <div className="check-flex">
            <CheckBox
              checked={values.primaryAccountHolder.changedDetails.mobileTelephone}
              onSelect={(val) => {
                if (!val) {
                  setFieldValue('primaryAccountHolder.changedDetails.mobileTelephoneNumber', '');
                }
                setFieldValue('primaryAccountHolder.changedDetails.mobileTelephone', val);
              }}
            />
            <p>Mobile Telephone Number</p>
          </div>
          {values.primaryAccountHolder.changedDetails.mobileTelephone && (
            <>
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Mobile Telephone Number"
                    id="primaryAccountHolder.changedDetails.mobileTelephoneNumber"
                    value={values.primaryAccountHolder.changedDetails.mobileTelephoneNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'primaryAccountHolder.changedDetails.mobileTelephoneNumber') &&
                        get(errors, 'primaryAccountHolder.changedDetails.mobileTelephoneNumber'),
                    )}
                  />
                  {Boolean(
                    get(touched, 'primaryAccountHolder.changedDetails.mobileTelephoneNumber') &&
                      get(errors, 'primaryAccountHolder.changedDetails.mobileTelephoneNumber'),
                  ) && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden">
                        Error: {get(errors, 'primaryAccountHolder.changedDetails.mobileTelephoneNumber')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="spc-30" />
            </>
          )}
          {/* Email */}
          <div className="check-flex">
            <CheckBox
              checked={values.primaryAccountHolder.changedDetails.email}
              onSelect={(val) => {
                if (!val) {
                  setFieldValue('primaryAccountHolder.changedDetails.emailAddress', '');
                }
                setFieldValue('primaryAccountHolder.changedDetails.email', val);
              }}
            />
            <p>Email</p>
          </div>
          {values.primaryAccountHolder.changedDetails.email && (
            <>
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Email"
                    id="primaryAccountHolder.changedDetails.emailAddress"
                    value={values.primaryAccountHolder.changedDetails.emailAddress}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type="email"
                    error={Boolean(
                      get(touched, 'primaryAccountHolder.changedDetails.emailAddress') &&
                        get(errors, 'primaryAccountHolder.changedDetails.emailAddress'),
                    )}
                  />
                  {Boolean(
                    get(touched, 'primaryAccountHolder.changedDetails.emailAddress') &&
                      get(errors, 'primaryAccountHolder.changedDetails.emailAddress'),
                  ) && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden">
                        Error: {get(errors, 'primaryAccountHolder.changedDetails.emailAddress')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="spc-30" />
            </>
          )}
          {/* Nationality */}
          <div className="check-flex">
            <CheckBox
              checked={values.primaryAccountHolder.changedDetails.nationalityType}
              onSelect={(val) => {
                if (!val) {
                  setFieldValue('primaryAccountHolder.changedDetails.nationality', '');
                }
                setFieldValue('primaryAccountHolder.changedDetails.nationalityType', val);
              }}
            />
            <p>Nationality</p>
          </div>
          {values.primaryAccountHolder.changedDetails.nationalityType && (
            <>
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Nationality"
                    id="primaryAccountHolder.changedDetails.nationality"
                    value={values.primaryAccountHolder.changedDetails.nationality}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'primaryAccountHolder.changedDetails.nationality') &&
                        get(errors, 'primaryAccountHolder.changedDetails.nationality'),
                    )}
                  />
                  {Boolean(
                    get(touched, 'primaryAccountHolder.changedDetails.nationality') &&
                      get(errors, 'primaryAccountHolder.changedDetails.nationality'),
                  ) && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden">
                        Error: {get(errors, 'primaryAccountHolder.changedDetails.nationality')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="spc-30" />
            </>
          )}
          {/* Occupation */}
          <div className="check-flex">
            <CheckBox
              checked={values.primaryAccountHolder.changedDetails.occupationType}
              onSelect={(val) => {
                if (!val) {
                  setFieldValue('primaryAccountHolder.changedDetails.occupation', '');
                }
                setFieldValue('primaryAccountHolder.changedDetails.occupationType', val);
              }}
            />
            <p>Occupation</p>
          </div>
          {values.primaryAccountHolder.changedDetails.occupationType && (
            <>
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Occupation"
                    id="primaryAccountHolder.changedDetails.occupation"
                    value={values.primaryAccountHolder.changedDetails.occupation}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'primaryAccountHolder.changedDetails.occupation') &&
                        get(errors, 'primaryAccountHolder.changedDetails.occupation'),
                    )}
                  />
                  {Boolean(
                    get(touched, 'primaryAccountHolder.changedDetails.occupation') &&
                      get(errors, 'primaryAccountHolder.changedDetails.occupation'),
                  ) && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden">
                        Error: {get(errors, 'primaryAccountHolder.changedDetails.occupation')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="spc-30" />
            </>
          )}
          {/* Annual income */}
          <div className="check-flex">
            <CheckBox
              checked={values.primaryAccountHolder.changedDetails.annualIncomeAmount}
              onSelect={(val) => {
                if (!val) {
                  setFieldValue('primaryAccountHolder.changedDetails.annualIncome', '');
                }
                setFieldValue('primaryAccountHolder.changedDetails.annualIncomeAmount', val);
              }}
            />
            <p>Annual Income</p>
          </div>
          {values.primaryAccountHolder.changedDetails.annualIncomeAmount && (
            <>
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Annual Income"
                    id="primaryAccountHolder.changedDetails.annualIncome"
                    value={values.primaryAccountHolder.changedDetails.annualIncome}
                    onChange={handleChange}
                    type="number"
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'primaryAccountHolder.changedDetails.annualIncome') &&
                        get(errors, 'primaryAccountHolder.changedDetails.annualIncome'),
                    )}
                  />
                  {Boolean(
                    get(touched, 'primaryAccountHolder.changedDetails.annualIncome') &&
                      get(errors, 'primaryAccountHolder.changedDetails.annualIncome'),
                  ) && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden">
                        Error: {get(errors, 'primaryAccountHolder.changedDetails.annualIncome')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="spc-30" />
            </>
          )}
          {/* Other income */}
          <div className="check-flex">
            <CheckBox
              checked={values.primaryAccountHolder.changedDetails.otherIncomeAmount}
              onSelect={(val) => {
                if (!val) {
                  setFieldValue('primaryAccountHolder.changedDetails.otherIncome', '');
                }
                setFieldValue('primaryAccountHolder.changedDetails.otherIncomeAmount', val);
              }}
            />
            <p>Other Income</p>
          </div>
          {values.primaryAccountHolder.changedDetails.otherIncomeAmount && (
            <>
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Other Income"
                    id="primaryAccountHolder.changedDetails.otherIncome"
                    value={values.primaryAccountHolder.changedDetails.otherIncome}
                    onChange={handleChange}
                    type="number"
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'primaryAccountHolder.changedDetails.otherIncome') &&
                        get(errors, 'primaryAccountHolder.changedDetails.otherIncome'),
                    )}
                  />
                  {Boolean(
                    get(touched, 'primaryAccountHolder.changedDetails.otherIncome') &&
                      get(errors, 'primaryAccountHolder.changedDetails.otherIncome'),
                  ) && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden">
                        Error: {get(errors, 'primaryAccountHolder.changedDetails.otherIncome')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="spc-30" />
            </>
          )}
          {/* SECOND HOLDER */}
          {values.secondaryAccountHolder && (
            <>
              <div className="spacer-responsive-30" />
              <div className="separator" />
              <div className="spacer-responsive-30" />
              <h3>Second Account Holder</h3>
              <div className="spc-18" />
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="First Name"
                    id="secondaryAccountHolder.firstName"
                    value={values.secondaryAccountHolder.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      get(touched, 'secondaryAccountHolder.firstName') &&
                      get(errors, 'secondaryAccountHolder.firstName')
                    }
                  />
                  {get(touched, 'secondaryAccountHolder.firstName') &&
                    get(errors, 'secondaryAccountHolder.firstName') && (
                      <div role="alert" aria-live="assertive" aria-relevant="all">
                        <span className="visually-hidden">
                          {' '}
                          Error: {get(errors, 'secondaryAccountHolder.firstName')}
                        </span>
                      </div>
                    )}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Last Name"
                    id="secondaryAccountHolder.lastName"
                    value={values.secondaryAccountHolder.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      get(touched, 'secondaryAccountHolder.lastName') && get(errors, 'secondaryAccountHolder.lastName')
                    }
                  />
                  {get(touched, 'secondaryAccountHolder.lastName') &&
                    get(errors, 'secondaryAccountHolder.lastName') && (
                      <div role="alert" aria-live="assertive" aria-relevant="all">
                        <span className="visually-hidden">Error: {get(errors, 'secondaryAccountHolder.lastName')}</span>
                      </div>
                    )}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="dd/mm/yyy"
                    id="secondaryAccountHolder.dateOfBirth"
                    value={values.secondaryAccountHolder.dateOfBirth}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      get(touched, 'secondaryAccountHolder.dateOfBirth') &&
                      get(errors, 'secondaryAccountHolder.dateOfBirth')
                    }
                  />
                  {get(touched, 'secondaryAccountHolder.dateOfBirth') &&
                    get(errors, 'secondaryAccountHolder.dateOfBirth') && (
                      <div role="alert" aria-live="assertive" aria-relevant="all">
                        <span className="visually-hidden">
                          {' '}
                          Error: {get(errors, 'secondaryAccountHolder.dateOfBirth')}
                        </span>
                      </div>
                    )}
                </div>
              </div>
              <div className="spc-30" />
              <p>
                <b>Have any of your details changed? If so please, complete, otherwise move to next section:</b>
              </p>
              <div className="spc-18" />

              {/* Home Telephone Number */}
              <div className="check-flex">
                <CheckBox
                  checked={values.secondaryAccountHolder.changedDetails.homeTelephone}
                  onSelect={(val) => {
                    if (!val) {
                      setFieldValue('secondaryAccountHolder.changedDetails.homeTelephoneNumber', '');
                    }
                    setFieldValue('secondaryAccountHolder.changedDetails.homeTelephone', val);
                  }}
                />
                <p>Home Telephone Number</p>
              </div>
              {values.secondaryAccountHolder.changedDetails.homeTelephone && (
                <>
                  <div className="input-grid">
                    <div className="input-grid-inner">
                      <TextFiled
                        placeholder="Home Telephone Number"
                        id="secondaryAccountHolder.changedDetails.homeTelephoneNumber"
                        value={values.secondaryAccountHolder.changedDetails.homeTelephoneNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(
                          get(touched, 'secondaryAccountHolder.changedDetails.homeTelephoneNumber') &&
                            get(errors, 'secondaryAccountHolder.changedDetails.homeTelephoneNumber'),
                        )}
                      />
                      {Boolean(
                        get(touched, 'secondaryAccountHolder.changedDetails.homeTelephoneNumber') &&
                          get(errors, 'secondaryAccountHolder.changedDetails.homeTelephoneNumber'),
                      ) && (
                        <div role="alert" aria-live="assertive" aria-relevant="all">
                          <span className="visually-hidden">
                            Error: {get(errors, 'secondaryAccountHolder.changedDetails.homeTelephoneNumber')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="spc-30" />
                </>
              )}

              {/* Mobile Telephone Number */}
              <div className="check-flex">
                <CheckBox
                  checked={values.secondaryAccountHolder.changedDetails.mobileTelephone}
                  onSelect={(val) => {
                    if (!val) {
                      setFieldValue('secondaryAccountHolder.changedDetails.mobileTelephoneNumber', '');
                    }
                    setFieldValue('secondaryAccountHolder.changedDetails.mobileTelephone', val);
                  }}
                />
                <p>Mobile Telephone Number</p>
              </div>
              {values.secondaryAccountHolder.changedDetails.mobileTelephone && (
                <>
                  <div className="input-grid">
                    <div className="input-grid-inner">
                      <TextFiled
                        placeholder="Mobile Telephone Number"
                        id="secondaryAccountHolder.changedDetails.mobileTelephoneNumber"
                        value={values.secondaryAccountHolder.changedDetails.mobileTelephoneNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(
                          get(touched, 'secondaryAccountHolder.changedDetails.mobileTelephoneNumber') &&
                            get(errors, 'secondaryAccountHolder.changedDetails.mobileTelephoneNumber'),
                        )}
                      />
                      {Boolean(
                        get(touched, 'secondaryAccountHolder.changedDetails.mobileTelephoneNumber') &&
                          get(errors, 'secondaryAccountHolder.changedDetails.mobileTelephoneNumber'),
                      ) && (
                        <div role="alert" aria-live="assertive" aria-relevant="all">
                          <span className="visually-hidden">
                            Error: {get(errors, 'secondaryAccountHolder.changedDetails.mobileTelephoneNumber')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="spc-30" />
                </>
              )}

              {/* Email */}
              <div className="check-flex">
                <CheckBox
                  checked={values.secondaryAccountHolder.changedDetails.email}
                  onSelect={(val) => {
                    if (!val) {
                      setFieldValue('secondaryAccountHolder.changedDetails.emailAddress', '');
                    }
                    setFieldValue('secondaryAccountHolder.changedDetails.email', val);
                  }}
                />
                <p>Email</p>
              </div>
              {values.secondaryAccountHolder.changedDetails.email && (
                <>
                  <div className="input-grid">
                    <div className="input-grid-inner">
                      <TextFiled
                        placeholder="Email"
                        id="secondaryAccountHolder.changedDetails.emailAddress"
                        value={values.secondaryAccountHolder.changedDetails.emailAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        type="email"
                        error={Boolean(
                          get(touched, 'secondaryAccountHolder.changedDetails.emailAddress') &&
                            get(errors, 'secondaryAccountHolder.changedDetails.emailAddress'),
                        )}
                      />
                      {Boolean(
                        get(touched, 'secondaryAccountHolder.changedDetails.emailAddress') &&
                          get(errors, 'secondaryAccountHolder.changedDetails.emailAddress'),
                      ) && (
                        <div role="alert" aria-live="assertive" aria-relevant="all">
                          <span className="visually-hidden">
                            Error: {get(errors, 'secondaryAccountHolder.changedDetails.emailAddress')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="spc-30" />
                </>
              )}

              {/* Nationality */}
              <div className="check-flex">
                <CheckBox
                  checked={values.secondaryAccountHolder.changedDetails.nationalityType}
                  onSelect={(val) => {
                    if (!val) {
                      setFieldValue('secondaryAccountHolder.changedDetails.nationality', '');
                    }
                    setFieldValue('secondaryAccountHolder.changedDetails.nationalityType', val);
                  }}
                />
                <p>Nationality</p>
              </div>
              {values.secondaryAccountHolder.changedDetails.nationalityType && (
                <>
                  <div className="input-grid">
                    <div className="input-grid-inner">
                      <TextFiled
                        placeholder="Nationality"
                        id="secondaryAccountHolder.changedDetails.nationality"
                        value={values.secondaryAccountHolder.changedDetails.nationality}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(
                          get(touched, 'secondaryAccountHolder.changedDetails.nationality') &&
                            get(errors, 'secondaryAccountHolder.changedDetails.nationality'),
                        )}
                      />
                      {Boolean(
                        get(touched, 'secondaryAccountHolder.changedDetails.nationality') &&
                          get(errors, 'secondaryAccountHolder.changedDetails.nationality'),
                      ) && (
                        <div role="alert" aria-live="assertive" aria-relevant="all">
                          <span className="visually-hidden">
                            Error: {get(errors, 'secondaryAccountHolder.changedDetails.nationality')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="spc-30" />
                </>
              )}

              {/* Occupation */}
              <div className="check-flex">
                <CheckBox
                  checked={values.secondaryAccountHolder.changedDetails.occupationType}
                  onSelect={(val) => {
                    if (!val) {
                      setFieldValue('secondaryAccountHolder.changedDetails.occupation', '');
                    }
                    setFieldValue('secondaryAccountHolder.changedDetails.occupationType', val);
                  }}
                />
                <p>Occupation</p>
              </div>
              {values.secondaryAccountHolder.changedDetails.occupationType && (
                <>
                  <div className="input-grid">
                    <div className="input-grid-inner">
                      <TextFiled
                        placeholder="Occupation"
                        id="secondaryAccountHolder.changedDetails.occupation"
                        value={values.secondaryAccountHolder.changedDetails.occupation}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(
                          get(touched, 'secondaryAccountHolder.changedDetails.occupation') &&
                            get(errors, 'secondaryAccountHolder.changedDetails.occupation'),
                        )}
                      />
                      {Boolean(
                        get(touched, 'secondaryAccountHolder.changedDetails.occupation') &&
                          get(errors, 'secondaryAccountHolder.changedDetails.occupation'),
                      ) && (
                        <div role="alert" aria-live="assertive" aria-relevant="all">
                          <span className="visually-hidden">
                            Error: {get(errors, 'secondaryAccountHolder.changedDetails.occupation')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="spc-30" />
                </>
              )}

              {/* Annual income */}
              <div className="check-flex">
                <CheckBox
                  checked={values.secondaryAccountHolder.changedDetails.annualIncomeAmount}
                  onSelect={(val) => {
                    if (!val) {
                      setFieldValue('secondaryAccountHolder.changedDetails.annualIncome', '');
                    }
                    setFieldValue('secondaryAccountHolder.changedDetails.annualIncomeAmount', val);
                  }}
                />
                <p>Annual Income</p>
              </div>
              {values.secondaryAccountHolder.changedDetails.annualIncomeAmount && (
                <>
                  <div className="input-grid">
                    <div className="input-grid-inner">
                      <TextFiled
                        placeholder="Annual Income"
                        id="secondaryAccountHolder.changedDetails.annualIncome"
                        value={values.secondaryAccountHolder.changedDetails.annualIncome}
                        onChange={handleChange}
                        type="number"
                        onKeyPress={(event) => {
                          if (!/[0-9]/.test(event.key)) {
                            event.preventDefault();
                          }
                        }}
                        onBlur={handleBlur}
                        error={Boolean(
                          get(touched, 'secondaryAccountHolder.changedDetails.annualIncome') &&
                            get(errors, 'secondaryAccountHolder.changedDetails.annualIncome'),
                        )}
                      />
                      {Boolean(
                        get(touched, 'secondaryAccountHolder.changedDetails.annualIncome') &&
                          get(errors, 'secondaryAccountHolder.changedDetails.annualIncome'),
                      ) && (
                        <div role="alert" aria-live="assertive" aria-relevant="all">
                          <span className="visually-hidden">
                            Error: {get(errors, 'secondaryAccountHolder.changedDetails.annualIncome')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="spc-30" />
                </>
              )}

              {/* Other income */}
              <div className="check-flex">
                <CheckBox
                  checked={values.secondaryAccountHolder.changedDetails.otherIncomeAmount}
                  onSelect={(val) => {
                    if (!val) {
                      setFieldValue('secondaryAccountHolder.changedDetails.otherIncome', '');
                    }
                    setFieldValue('secondaryAccountHolder.changedDetails.otherIncomeAmount', val);
                  }}
                />
                <p>Other Income</p>
              </div>
              {values.secondaryAccountHolder.changedDetails.otherIncomeAmount && (
                <>
                  <div className="input-grid">
                    <div className="input-grid-inner">
                      <TextFiled
                        placeholder="Other Income"
                        id="secondaryAccountHolder.changedDetails.otherIncome"
                        value={values.secondaryAccountHolder.changedDetails.otherIncome}
                        onChange={handleChange}
                        type="number"
                        onKeyPress={(event) => {
                          if (!/[0-9]/.test(event.key)) {
                            event.preventDefault();
                          }
                        }}
                        onBlur={handleBlur}
                        error={Boolean(
                          get(touched, 'secondaryAccountHolder.changedDetails.otherIncome') &&
                            get(errors, 'secondaryAccountHolder.changedDetails.otherIncome'),
                        )}
                      />
                      {Boolean(
                        get(touched, 'secondaryAccountHolder.changedDetails.otherIncome') &&
                          get(errors, 'secondaryAccountHolder.changedDetails.otherIncome'),
                      ) && (
                        <div role="alert" aria-live="assertive" aria-relevant="all">
                          <span className="visually-hidden">
                            Error: {get(errors, 'secondaryAccountHolder.changedDetails.otherIncome')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="spc-30" />
                </>
              )}
            </>
          )}
          <div className="spacer-responsive-30" />
          <div className="separator" />
          <div className="spacer-responsive-30" />
          <p>
            <b>Please confirm the source of the overpayment, providing the information requested.</b>
          </p>
          <div className="spacer-responsive-30" />
          {/* Savings/Investments */}
          <div className="check-flex">
            <Switch
              checked={values.sources.sourceSavingsOrInvestments}
              onSelect={(val) => {
                setFieldValue('sources.sourceSavingsOrInvestments', val);
              }}
            />
            <p>Savings/Investments</p>
          </div>
          {values.sources.sourceSavingsOrInvestments && (
            <div className="additional-fields">
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Date funds received (dd/mm/yyyy)"
                    id="sources.savingsOrInvestments.dateFundsReceived"
                    value={values.sources.savingsOrInvestments.dateFundsReceived}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.savingsOrInvestments.dateFundsReceived') &&
                        get(errors, 'sources.savingsOrInvestments.dateFundsReceived'),
                    )}
                  />
                  {touched.sources?.savingsOrInvestments?.dateFundsReceived &&
                    !!errors.sources?.savingsOrInvestments?.dateFundsReceived && (
                      <div role="alert" aria-live="assertive" aria-relevant="all">
                        <span className="visually-hidden">
                          Error: {errors.sources?.savingsOrInvestments?.dateFundsReceived}
                        </span>
                      </div>
                    )}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    type="number"
                    step="1"
                    onKeyDown={(e) => ['.', 'e', '+', '-'].includes(e.key) && e.preventDefault()}
                    placeholder="Amount received"
                    id="sources.savingsOrInvestments.amountReceived"
                    value={values.sources.savingsOrInvestments.amountReceived}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.savingsOrInvestments.amountReceived') &&
                        get(errors, 'sources.savingsOrInvestments.amountReceived'),
                    )}
                  />
                  <div className="input-endorment">£</div>
                  {/* {touched.primaryAccountHolder?.firstName && !!errors.primaryAccountHolder?.firstName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.firstName}</span>
                    </div>
                  )} */}
                </div>
              </div>
              <div className="spc18"></div>
              <div className="input-grid-inner">
                <TextFiled
                  placeholder="From which company?"
                  id="sources.savingsOrInvestments.fromCompany"
                  value={values.sources.savingsOrInvestments.fromCompany}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(
                    get(touched, 'sources.savingsOrInvestments.fromCompany') &&
                      get(errors, 'sources.savingsOrInvestments.fromCompany'),
                  )}
                />
                {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
              </div>
              <div className="spc18"></div>
              <div className="input-grid-inner">
                <TextFiled
                  placeholder="How were the savings accumulated?"
                  id="sources.savingsOrInvestments.savingsAccumulationMethod"
                  value={values.sources.savingsOrInvestments.savingsAccumulationMethod}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(
                    get(touched, 'sources.savingsOrInvestments.savingsAccumulationMethod') &&
                      get(errors, 'sources.savingsOrInvestments.savingsAccumulationMethod'),
                  )}
                />
                {/* {touched.primaryAccountHolder?.dateOfBirth && !!errors.primaryAccountHolder?.dateOfBirth && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.dateOfBirth}</span>
                    </div>
                  )} */}
              </div>

              <div className="spacer-responsive-30" />
            </div>
          )}
          {/* Income from Employment or Bonus */}
          <div className="check-flex">
            <Switch
              checked={values.sources.sourceEmploymentOrBonus}
              onSelect={(val) => setFieldValue('sources.sourceEmploymentOrBonus', val)}
            />
            <p>Income from Employment or Bonus</p>
          </div>
          {values.sources.sourceEmploymentOrBonus && (
            <div className="additional-fields">
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Date received (dd/mm/yyyy)"
                    id="sources.incomeFromEmploymentOrBonus.dateReceived"
                    value={values.sources.incomeFromEmploymentOrBonus.dateReceived}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.incomeFromEmploymentOrBonus.dateReceived') &&
                        get(errors, 'sources.incomeFromEmploymentOrBonus.dateReceived'),
                    )}
                  />
                  {/* {touched.primaryAccountHolder?.dateOfBirth && !!errors.primaryAccountHolder?.dateOfBirth && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.dateOfBirth}</span>
                    </div>
                  )} */}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    type="number"
                    placeholder="Amount received "
                    step="1"
                    onKeyDown={(e) => ['.', 'e', '+', '-'].includes(e.key) && e.preventDefault()}
                    id="sources.incomeFromEmploymentOrBonus.amountReceived"
                    value={values.sources.incomeFromEmploymentOrBonus.amountReceived}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.incomeFromEmploymentOrBonus.amountReceived') &&
                        get(errors, 'sources.incomeFromEmploymentOrBonus.amountReceived'),
                    )}
                  />
                  <div className="input-endorment">£</div>
                  {/* {touched.primaryAccountHolder?.firstName && !!errors.primaryAccountHolder?.firstName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.firstName}</span>
                    </div>
                  )} */}
                </div>
              </div>
              <div className="spc18"></div>
              <div className="input-grid-inner">
                <TextFiled
                  placeholder="From which employer?"
                  id="sources.incomeFromEmploymentOrBonus.fromEmployer"
                  value={values.sources.incomeFromEmploymentOrBonus.fromEmployer}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(
                    get(touched, 'sources.incomeFromEmploymentOrBonus.fromEmployer') &&
                      get(errors, 'sources.incomeFromEmploymentOrBonus.fromEmployer'),
                  )}
                />
                {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
              </div>

              <div className="spacer-responsive-30" />
            </div>
          )}

          {/* Sale of Property */}
          <div className="check-flex">
            <Switch
              checked={values.sources.sourceSaleOfProperty}
              onSelect={(val) => setFieldValue('sources.sourceSaleOfProperty', val)}
            />
            <p>Sale of Property</p>
          </div>
          {values.sources.sourceSaleOfProperty && (
            <div className="additional-fields">
              <div className="input-grid-inner">
                <TextFiled
                  placeholder="Address line 1 "
                  id="sources.saleOfProperty.line1"
                  value={values.sources.saleOfProperty.line1}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(
                    get(touched, 'sources.saleOfProperty.line1') && get(errors, 'sources.saleOfProperty.line1'),
                  )}
                />
                {/* {touched.primaryAccountHolder?.dateOfBirth && !!errors.primaryAccountHolder?.dateOfBirth && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.dateOfBirth}</span>
                    </div>
                  )} */}
              </div>
              <div className="spc18"></div>
              <div className="input-grid-inner">
                <TextFiled
                  placeholder="Address line 2 "
                  id="sources.saleOfProperty.line2"
                  value={values.sources.saleOfProperty.line2}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(
                    get(touched, 'sources.saleOfProperty.line2') && get(errors, 'sources.saleOfProperty.line2'),
                  )}
                />
                {/* {touched.primaryAccountHolder?.dateOfBirth && !!errors.primaryAccountHolder?.dateOfBirth && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.dateOfBirth}</span>
                    </div>
                  )} */}
              </div>
              <div className="spc18"></div>
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="City "
                    id="sources.saleOfProperty.city"
                    value={values.sources.saleOfProperty.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.saleOfProperty.city') && get(errors, 'sources.saleOfProperty.city'),
                    )}
                  />
                  {/* {touched.primaryAccountHolder?.firstName && !!errors.primaryAccountHolder?.firstName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.firstName}</span>
                    </div>
                  )} */}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="State/Province"
                    id="sources.saleOfProperty.province"
                    value={values.sources.saleOfProperty.province}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.saleOfProperty.province') && get(errors, 'sources.saleOfProperty.province'),
                    )}
                  />
                  {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Zip/Postal"
                    id="sources.saleOfProperty.postcode"
                    value={values.sources.saleOfProperty.postcode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.saleOfProperty.postcode') && get(errors, 'sources.saleOfProperty.postcode'),
                    )}
                  />
                  {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
                </div>
                <div className="input-grid-inner">
                  <Select
                    id="sources.saleOfProperty.country"
                    value={values.sources.saleOfProperty.country}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.saleOfProperty.country') && get(errors, 'sources.saleOfProperty.country'),
                    )}
                    $hasValue={!!get(values, 'sources.saleOfProperty.country')}
                  >
                    <option value="" disabled selected>
                      Country
                    </option>
                    {countries.map((el) => (
                      <option key={el.name} value={el.name}>
                        {el.name}
                      </option>
                    ))}
                  </Select>
                  {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Date property sold (dd/mm/yyyy)"
                    id="sources.saleOfProperty.datePropertySold"
                    value={values.sources.saleOfProperty.datePropertySold}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.saleOfProperty.datePropertySold') &&
                        get(errors, 'sources.saleOfProperty.datePropertySold'),
                    )}
                  />
                  {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    type="number"
                    step="1"
                    onKeyDown={(e) => ['.', 'e', '+', '-'].includes(e.key) && e.preventDefault()}
                    placeholder="Amount of sale proceeds received"
                    id="sources.saleOfProperty.saleProceedsReceived"
                    value={values.sources.saleOfProperty.saleProceedsReceived}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.saleOfProperty.saleProceedsReceived') &&
                        get(errors, 'sources.saleOfProperty.saleProceedsReceived'),
                    )}
                  />
                  <div className="input-endorment">£</div>
                  {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
                </div>
              </div>
              <div className="spacer-responsive-30" />
            </div>
          )}
          {/* Inheritance */}
          <div className="check-flex">
            <Switch
              checked={values.sources.sourceInheritance}
              onSelect={(val) => setFieldValue('sources.sourceInheritance', val)}
            />
            <p>Inheritance</p>
          </div>
          {values.sources.sourceInheritance && (
            <div className="additional-fields">
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Date received (dd/mm/yyyy)"
                    id="sources.inheritance.dateFundsReceived"
                    value={values.sources.inheritance.dateFundsReceived}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.inheritance.dateFundsReceived') &&
                        get(errors, 'sources.inheritance.dateFundsReceived'),
                    )}
                  />
                  {/* {touched.primaryAccountHolder?.dateOfBirth && !!errors.primaryAccountHolder?.dateOfBirth && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.dateOfBirth}</span>
                    </div>
                  )} */}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    type="number"
                    step="1"
                    onKeyDown={(e) => ['.', 'e', '+', '-'].includes(e.key) && e.preventDefault()}
                    placeholder="Amount received "
                    id="sources.inheritance.amountReceived"
                    value={values.sources.inheritance.amountReceived}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.inheritance.amountReceived') &&
                        get(errors, 'sources.sources.inheritance.amountReceived'),
                    )}
                  />
                  <div className="input-endorment">£</div>
                  {/* {touched.primaryAccountHolder?.firstName && !!errors.primaryAccountHolder?.firstName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.firstName}</span>
                    </div>
                  )} */}
                </div>
              </div>
              <div className="spc18"></div>
              <div className="input-grid-inner">
                <TextFiled
                  placeholder="Payment received from"
                  id="sources.inheritance.from"
                  value={values.sources.inheritance.from}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(
                    get(touched, 'sources.inheritance.from') && get(errors, 'sources.sources.inheritanceDetails.from'),
                  )}
                />
                {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
              </div>

              <div className="spacer-responsive-30" />
            </div>
          )}

          {/* Gift */}
          <div className="check-flex">
            <Switch checked={values.sources.sourceGift} onSelect={(val) => setFieldValue('sources.sourceGift', val)} />
            <p>Gift</p>
          </div>
          {values.sources.sourceGift && (
            <div className="additional-fields">
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Date funds received (dd/mm/yyyy)"
                    id="sources.gift.dateFundsReceived"
                    value={values.sources.gift.dateFundsReceived}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.gift.dateFundsReceived') && get(errors, 'sources.gift.dateFundsReceived'),
                    )}
                  />
                  {touched.sources?.gift?.dateFundsReceived && !!errors.sources?.gift?.dateFundsReceived && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.sources?.gift?.dateFundsReceived}</span>
                    </div>
                  )}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    type="number"
                    step="1"
                    onKeyDown={(e) => ['.', 'e', '+', '-'].includes(e.key) && e.preventDefault()}
                    placeholder="Amount received "
                    id="sources.gift.amountReceived"
                    value={values.sources.gift.amountReceived}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.gift.amountReceived') && get(errors, 'sources.gift.amountReceived'),
                    )}
                  />
                  <div className="input-endorment">£</div>
                  {/* {touched.primaryAccountHolder?.firstName && !!errors.primaryAccountHolder?.firstName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.firstName}</span>
                    </div>
                  )} */}
                </div>
              </div>
              <div className="spc-18"></div>
              <div className="input-grid-inner">
                <TextFiled
                  placeholder="Reason for the gift"
                  id="sources.gift.giftReason"
                  value={values.sources.gift.giftReason}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(get(touched, 'sources.gift.giftReason') && get(errors, 'sources.gift.giftReason'))}
                />
                {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
              </div>
              <div className="spc-18"></div>
              <div className="input-grid-inner">
                <TextFiled
                  placeholder="What is the relationship between the mortgage holder(s) and the Giftor?"
                  id="sources.gift.relationship"
                  value={values.sources.gift.relationship}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(get(touched, 'sources.gift.relationship') && get(errors, 'sources.gift.relationship'))}
                />
                {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
              </div>
              <div className="spc-18"></div>
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Name of the giftor"
                    id="sources.gift.giftorName"
                    value={values.sources.gift.giftorName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(get(touched, 'sources.gift.giftorName') && get(errors, 'sources.gift.giftorName'))}
                  />
                  {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Giftor date of birth (dd/mm/yyyy)"
                    id="sources.gift.giftorDateOfBirth"
                    value={values.sources.gift.giftorDateOfBirth}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.gift.giftorDateOfBirth') && get(errors, 'sources.gift.giftorDateOfBirth'),
                    )}
                  />
                  {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
                </div>
              </div>
              <div className="spc-18"></div>
              <div className="input-grid-inner">
                <TextFiled
                  placeholder="Address of the giftor line 1"
                  id="sources.gift.giftorAddress.line1"
                  value={values.sources.gift.giftorAddress.line1}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(
                    get(touched, 'sources.gift.giftorAddress.line1') && get(errors, 'sources.gift.giftorAddress.line1'),
                  )}
                />
                {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
              </div>
              <div className="spc-18"></div>
              <div className="input-grid-inner">
                <TextFiled
                  placeholder="Address of the giftor line 2"
                  id="sources.gift.giftorAddress.line2"
                  value={values.sources.gift.giftorAddress.line2}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(
                    get(touched, 'sources.gift.giftorAddress.line2') && get(errors, 'sources.gift.giftorAddress.line2'),
                  )}
                />
                {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
              </div>
              <div className="spc-18"></div>
              <div className="input-grid">
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="City"
                    id="sources.gift.giftorAddress.city"
                    value={values.sources.gift.giftorAddress.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.gift.giftorAddress.city') && get(errors, 'sources.gift.giftorAddress.city'),
                    )}
                  />
                  {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="State/Province"
                    id="sources.gift.giftorAddress.county"
                    value={values.sources.gift.giftorAddress.county}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.gift.giftorAddress.county') &&
                        get(errors, 'sources.gift.giftorAddress.county'),
                    )}
                  />
                  {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Zip/Postal"
                    id="sources.gift.giftorAddress.postcode"
                    value={values.sources.gift.giftorAddress.postcode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.gift.giftorAddress.postcode') &&
                        get(errors, 'sources.gift.giftorAddress.postcode'),
                    )}
                  />
                  {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
                </div>
                <div className="input-grid-inner">
                  <Select
                    id="sources.gift.giftorAddress.country"
                    value={values.sources.gift.giftorAddress.country}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      get(touched, 'sources.gift.giftorAddress.country') &&
                        get(errors, 'sources.gift.giftorAddress.country'),
                    )}
                    $hasValue={!!get(values, 'sources.gift.giftorAddress.country')}
                  >
                    <option value="" disabled selected>
                      Country
                    </option>
                    {countries.map((el) => (
                      <option key={el.name} value={el.name}>
                        {el.name}
                      </option>
                    ))}
                  </Select>
                  {/* {touched.primaryAccountHolder?.lastName && !!errors.primaryAccountHolder?.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.primaryAccountHolder?.lastName}</span>
                    </div>
                  )} */}
                </div>
              </div>

              <div className="spacer-responsive-30" />
            </div>
          )}

          {/* Other */}
          <div className="check-flex">
            <Switch
              checked={values.sources.otherSource}
              onSelect={(val) => setFieldValue('sources.otherSource', val)}
            />
            <p>Other</p>
          </div>
          {values.sources.otherSource && (
            <div className="additional-fields">
              <div className="input-grid-full">
                <TextArea
                  placeholder="For all other sources such as compensation, loans, lottery wins etc. Please provide full details including the amount, how the funds were obtained and the date the funds were received, along with the person or organisation funds were received from."
                  rows={5}
                  id="sources.other"
                  value={values.sources.other}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(get(touched, 'sources.other') && get(errors, 'sources.other'))}
                />
              </div>
              <div className="spacer-responsive-30" />
            </div>
          )}

          <div className="spacer-responsive-30" />
          <p>
            <b>Your Personal Information and what we do with it</b>
          </p>
          <div className="spacer-responsive-30" />
          <p>
            We are a data controller of your personal information. The way that we process your personal information is
            described in our Privacy Notice. This information is also available on our website{' '}
            <a className="link-text" href="/privacy-notice" target="_blank">
              <b>
                <u>www.chorleybs.co.uk/privacy</u>
              </b>
            </a>
            .
          </p>
          <div className="spacer-responsive-30" />
          <button className="btn" type="submit" disabled={!isValid || isSubmitting}>
            Submit
          </button>
        </form>
      ) : (
        <div className="response">
          <div className="response-card">
            <div className="spacer-responsive-30" />
            <div className="spacer-responsive-30" />
            <h4>Submitted</h4>
            <div className="spacer-responsive-30" />
            <p>Thank you for contacting us.  A member of our team will be in touch soon.</p>
            <div className="spacer-responsive-30" />
            <button className="btn" onClick={() => setIsFormSubmitted(false)}>
              OK
            </button>
            <div className="spacer-responsive-30" />
            <div className="spacer-responsive-30" />
          </div>
        </div>
      )}
    </Section>
  );
};

export default App;
